import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  Printer,
  Check,
  AlertCircle,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  Cloud,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { useRegisterSettlement, formatPaymentBreakdown } from '@/hooks/useRegisterSettlement';

// Jamaica Dollar denominations
const JMD_DENOMINATIONS = [
  { value: 0.01, label: '$0.01' },
  { value: 0.05, label: '$0.05' },
  { value: 0.10, label: '$0.10' },
  { value: 0.25, label: '$0.25' },
  { value: 1.00, label: '$1.00' },
  { value: 5.00, label: '$5.00' },
  { value: 10.00, label: '$10.00' },
  { value: 20.00, label: '$20.00' },
  { value: 50.00, label: '$50.00' },
  { value: 100.00, label: '$100.00' },
  { value: 500.00, label: '$500.00' },
  { value: 1000.00, label: '$1,000.00' },
  { value: 5000.00, label: '$5,000.00' },
];

export default function CloseRegister() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { user } = useAuth();
  const { effectiveRole } = usePermissions();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  // Check if user can settle registers (supervisor+ only)
  const canSettleRegister = ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(effectiveRole || '');

  // Denomination quantities
  const [denominations, setDenominations] = useState<Record<string, number>>({});

  // Payment type counted amounts
  const [cashCounted, setCashCounted] = useState<string>('');

  // Cash float inputs
  const [closingFloat, setClosingFloat] = useState<string>('');
  const [cashToBank, setCashToBank] = useState<string>('');

  // Expanded sections
  const [cashExpanded, setCashExpanded] = useState(false);
  const [cardExpanded, setCardExpanded] = useState(false);

  const [isClosing, setIsClosing] = useState(false);

  // Manager PIN dialog for settlement approval
  const [showSettlementPinDialog, setShowSettlementPinDialog] = useState(false);

  // Settlement data with payment breakdown
  const { fetchSettlementData } = useRegisterSettlement();
  const { data: settlementData } = useQuery({
    queryKey: ['settlement-data', sessionId],
    queryFn: () => sessionId ? fetchSettlementData(sessionId) : null,
    enabled: !!sessionId,
    staleTime: 1000 * 10,
  });

  // Format payment breakdown for display
  const paymentBreakdownFormatted = useMemo(() => {
    if (!settlementData?.payment_breakdown) return [];
    return formatPaymentBreakdown(settlementData.payment_breakdown);
  }, [settlementData?.payment_breakdown]);

  // Fetch session details
  const { data: session, isLoading } = useQuery({
    queryKey: ['cash-drawer-session', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      
      const { data, error } = await supabase
        .from('cash_drawer_sessions')
        .select(`
          *,
          register:registers(name),
          user:profiles(first_name, last_name),
          location:locations(name)
        `)
        .eq('id', sessionId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!sessionId,
  });

  // Check if this is a virtual session
  const isVirtualSession = settlementData?.session_type === 'virtual' || session?.session_type === 'virtual';

  // Fetch items sold during this session
  const { data: itemsSold } = useQuery({
    queryKey: ['session-items-sold', sessionId],
    queryFn: async () => {
      if (!session) return [];
      
      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_items(name, quantity, total)
        `)
        .eq('location_id', session.location_id)
        .eq('user_id', session.user_id)
        .gte('created_at', session.opened_at)
        .lte('created_at', session.closed_at || new Date().toISOString())
        .in('status', ['completed', 'ready', 'sent', 'in_progress']);

      if (error) throw error;

      const itemMap = new Map<string, { name: string; quantity: number; total: number }>();
      
      orders?.forEach(order => {
        (order.order_items as any[])?.forEach(item => {
          const existing = itemMap.get(item.name);
          if (existing) {
            existing.quantity += item.quantity;
            existing.total += item.total;
          } else {
            itemMap.set(item.name, {
              name: item.name,
              quantity: item.quantity,
              total: item.total,
            });
          }
        });
      });

      return Array.from(itemMap.values()).sort((a, b) => b.quantity - a.quantity);
    },
    enabled: !!session,
  });

  // Fetch cash movements during session
  const { data: cashMovements } = useQuery({
    queryKey: ['cash-movements', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      
      const { data, error } = await supabase
        .from('cash_movements')
        .select(`
          *,
          user:profiles(first_name, last_name)
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!sessionId,
  });

  // Calculate totals
  const cashTotal = useMemo(() => {
    return Object.entries(denominations).reduce((sum, [value, qty]) => {
      return sum + parseFloat(value) * qty;
    }, 0);
  }, [denominations]);

  const expectedCash = session?.expected_balance ?? (
    (session?.opening_balance || 0) + 
    (session?.cash_payments || 0) - 
    (session?.cash_refunds || 0) + 
    (session?.pay_ins || 0) - 
    (session?.pay_outs || 0)
  );

  const countedCash = parseFloat(cashCounted) || 0;
  const cashDifference = countedCash - expectedCash;

  // Total calculations
  const totalExpected = expectedCash; // Add other payment types as needed
  const totalCounted = countedCash; // Cash is the only manually counted payment type
  const totalDifference = totalCounted - totalExpected;

  // Update denomination quantity
  const updateDenomination = (value: string, qty: number) => {
    const newDenominations = {
      ...denominations,
      [value]: Math.max(0, qty),
    };
    setDenominations(newDenominations);
    
    // Auto-update cash counted based on denominations
    const total = Object.entries(newDenominations).reduce((sum, [v, q]) => {
      return sum + parseFloat(v) * q;
    }, 0);
    setCashCounted(total.toFixed(2));
  };

  // Close register mutation
  const closeRegister = useMutation({
    mutationFn: async ({ approvedBy }: { approvedBy?: string } = {}) => {
      if (!sessionId || !session) throw new Error('No session');

      // Close + settle atomically via the canonical RPC. It is SECURITY DEFINER
      // and computes the expected balance, closure number and variance server-side,
      // which avoids the silent failures of the previous multi-step direct update.
      const { error } = await supabase.rpc('settle_register_session_v2', {
        p_session_id: sessionId,
        p_closing_balance: countedCash,
        p_denominations: denominations,
        p_payment_type_counted: {
          cash: { expected: expectedCash, counted: countedCash },
        },
        p_settled_by: user?.id ?? null,
        p_approved_by: approvedBy ?? user?.id ?? null,
      });

      if (error) throw error;

      // Best-effort: record closing float / cash-to-bank movements. The session is
      // already settled at this point, so a failure here must not block the close.
      try {
        if (parseFloat(closingFloat) > 0) {
          await supabase.from('cash_movements').insert({
            session_id: sessionId,
            user_id: user?.id,
            amount: parseFloat(closingFloat),
            movement_type: 'closing_float',
            reason: 'Closing float left in drawer',
          });
        }

        if (parseFloat(cashToBank) > 0) {
          await supabase.from('cash_movements').insert({
            session_id: sessionId,
            user_id: user?.id,
            amount: parseFloat(cashToBank),
            movement_type: 'cash_to_bank',
            reason: 'Cash sent to bank',
          });
        }
      } catch (movementError) {
        console.error('Failed to record cash movement(s) after settlement:', movementError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-session'] });
      queryClient.invalidateQueries({ queryKey: ['register-closures'] });
      toast.success('Register Closed', {
        description: 'Register has been closed successfully',
      });
      navigate('/register-closures');
    },
    onError: (err: any) => {
      toast.error('Failed to close register', {
        description: err?.message || 'Something went wrong while closing the register. Please try again.',
      });
    },
    onSettled: () => {
      setIsClosing(false);
    },
  });

  // Print closure summary
  const handlePrintSummary = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const cashierName = session?.user 
      ? `${(session.user as any).first_name || ''} ${(session.user as any).last_name || ''}`.trim()
      : 'Unknown';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Register Closure Summary</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; padding: 20px; max-width: 300px; margin: 0 auto; }
          h1 { font-size: 14px; text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .header { margin-bottom: 20px; }
          .header p { margin: 4px 0; }
          .divider { border-top: 1px dashed #000; margin: 15px 0; }
          .section-title { font-weight: bold; margin: 15px 0 10px; }
          .item { display: flex; justify-content: space-between; margin: 4px 0; }
          .item-name { flex: 1; }
          .item-qty { text-align: right; min-width: 50px; }
          .total { font-weight: bold; border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; font-size: 10px; }
        </style>
      </head>
      <body>
        <h1>REGISTER CLOSURE SUMMARY</h1>
        
        <div class="header">
          <p><strong>Outlet:</strong> ${session?.location?.name || currentLocation?.name}</p>
          <p><strong>Register:</strong> ${session?.register?.name || 'Register'}</p>
          <p><strong>Closure #:</strong> ${session?.closure_number || 'Pending'}</p>
          <p><strong>Cashier:</strong> ${cashierName}</p>
          <p><strong>Opened:</strong> ${session ? new Date(session.opened_at).toLocaleString() : ''}</p>
          <p><strong>Closed:</strong> ${new Date().toLocaleString()}</p>
        </div>
        
        <div class="divider"></div>
        
        <div class="section-title">ITEMS SOLD THIS SHIFT:</div>
        ${itemsSold?.map(item => `
          <div class="item">
            <span class="item-name">${item.name}</span>
            <span class="item-qty">${item.quantity}</span>
          </div>
        `).join('') || '<p>No items sold</p>'}
        
        <div class="total">
          <div class="item">
            <span>Total Items:</span>
            <span>${itemsSold?.reduce((sum, item) => sum + item.quantity, 0) || 0}</span>
          </div>
        </div>
        
        <div class="divider"></div>
        
        <div class="section-title">CASH SUMMARY:</div>
        <div class="item">
          <span>Expected:</span>
          <span>${formatCurrency(expectedCash)}</span>
        </div>
        <div class="item">
          <span>Counted:</span>
          <span>${formatCurrency(countedCash)}</span>
        </div>
        <div class="item" style="font-weight: bold; color: ${cashDifference < 0 ? 'red' : cashDifference > 0 ? 'green' : 'black'}">
          <span>Difference:</span>
          <span>${cashDifference >= 0 ? '+' : ''}${formatCurrency(cashDifference)}</span>
        </div>
        
        <div class="footer">
          <p>*** END OF REPORT ***</p>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleClose = () => {
    if (!canSettleRegister) {
      toast.error('Permission Denied', {
        description: 'Only supervisors and managers can settle registers',
      });
      return;
    }
    // Show manager PIN dialog for approval
    setShowSettlementPinDialog(true);
  };

  const handleSettlementApproved = async (managerId: string, _managerName: string) => {
    setShowSettlementPinDialog(false);
    setIsClosing(true);
    closeRegister.mutate({ approvedBy: managerId });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-white animate-spin" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-white/50 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">Session Not Found</h3>
          <p className="text-white/60 mb-6">The register session could not be found</p>
          <Button variant="secondary" onClick={() => navigate('/register-closures')}>
            Back to Register Closures
          </Button>
        </div>
      </div>
    );
  }

  const openingTime = new Date(session.opened_at);
  const cashierName = session.user 
    ? `${(session.user as any).first_name || ''} ${(session.user as any).last_name || ''}`.trim()
    : 'Unknown';

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-8 py-6">
        <h1 className="text-3xl font-light">Close register</h1>
        <p className="text-white/60 mt-2">
          Close your register to finalize payments and sales for the day.
        </p>
      </header>

      <div className="max-w-5xl mx-auto px-8 py-8 space-y-10">
        {/* Register Details */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Register details</h2>
            {isVirtualSession && (
              <Badge variant="secondary" className="gap-1 bg-blue-500/20 text-blue-400 border-blue-500/30">
                <Cloud className="h-3 w-3" />
                Virtual Session
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-4 gap-8">
            <div>
              <span className="text-white/60 text-sm">Outlet</span>
              <p className="text-white mt-1">{session.location?.name || currentLocation?.name}</p>
            </div>
            <div>
              <span className="text-white/60 text-sm">{isVirtualSession ? 'Session Type' : 'Register'}</span>
              <p className="text-white mt-1">
                {isVirtualSession ? 'Virtual (Admin)' : session.register?.name || 'REGISTER'}
              </p>
            </div>
            <div>
              <span className="text-white/60 text-sm">Closure #</span>
              <p className="text-white mt-1">{session.closure_number || 'Pending'}</p>
            </div>
            <div>
              <span className="text-white/60 text-sm">Opening time</span>
              <p className="text-white mt-1">
                {openingTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at{' '}
                {openingTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </p>
            </div>
          </div>
        </section>

        <hr className="border-white/10" />

        {/* Count Cash */}
        <section className="grid grid-cols-[250px_1fr] gap-8">
          <div>
            <h2 className="text-lg font-medium">Count cash</h2>
            <p className="text-white/60 text-sm mt-2">
              Enter the amount from the till.
            </p>
          </div>
          
          <div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-3 text-white/80 font-medium">Denomination</th>
                  <th className="text-center py-3 text-white/80 font-medium">Quantity</th>
                  <th className="text-right py-3 text-white/80 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {JMD_DENOMINATIONS.map((denom) => {
                  const qty = denominations[denom.value.toString()] || 0;
                  const amount = qty * denom.value;
                  
                  return (
                    <tr key={denom.value} className="border-b border-white/10">
                      <td className="py-3 text-white">{denom.label}</td>
                      <td className="py-3">
                        <Input
                          type="number"
                          min="0"
                          value={qty || ''}
                          onChange={(e) => updateDenomination(
                            denom.value.toString(), 
                            parseInt(e.target.value) || 0
                          )}
                          className="w-24 mx-auto text-center bg-[#2a2a3e] border-[#3a3a4e] text-white"
                          placeholder="0"
                        />
                      </td>
                      <td className="py-3 text-right text-white tabular-nums">
                        {formatCurrency(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-white/30">
                  <td colSpan={2} className="py-4 font-semibold text-white">CASH TOTAL</td>
                  <td className="py-4 text-right font-semibold text-white tabular-nums">
                    {formatCurrency(cashTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <hr className="border-white/10" />

        {/* Payments Summary */}
        <section className="grid grid-cols-[250px_1fr] gap-8">
          <div>
            <h2 className="text-lg font-medium">Payments summary</h2>
            <p className="text-white/60 text-sm mt-2">
              Balance your register by entering the amount counted from the till and other payment terminals into the empty fields here.
            </p>
          </div>
          
          <div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-3 text-white/80 font-medium">Payment types</th>
                  <th className="text-right py-3 text-white/80 font-medium">Expected (J$)</th>
                  <th className="text-center py-3 text-white/80 font-medium">Counted (J$)</th>
                  <th className="text-right py-3 text-white/80 font-medium">Differences (J$)</th>
                </tr>
              </thead>
              <tbody>
                {/* Cash Row - Expandable */}
                <tr className="border-b border-white/10">
                  <td className="py-4">
                    <button 
                      onClick={() => setCashExpanded(!cashExpanded)}
                      className="text-left"
                    >
                      <div className="font-medium text-white">Cash</div>
                      <div className="text-sm text-blue-400 hover:underline">
                        View cash payments, floats and movements
                      </div>
                    </button>
                  </td>
                  <td className="py-4 text-right text-white tabular-nums">
                    {expectedCash.toFixed(2)}
                  </td>
                  <td className="py-4">
                    <Input
                      type="number"
                      step="0.01"
                      value={cashCounted}
                      onChange={(e) => setCashCounted(e.target.value)}
                      className="w-28 mx-auto text-center bg-[#2a2a3e] border-[#3a3a4e] text-white"
                      placeholder="0.00"
                    />
                  </td>
                  <td className={cn(
                    "py-4 text-right tabular-nums font-medium",
                    cashDifference > 0 && "text-green-400",
                    cashDifference < 0 && "text-red-400",
                    cashDifference === 0 && "text-white/60"
                  )}>
                    {cashDifference !== 0 ? (cashDifference > 0 ? '+' : '') + cashDifference.toFixed(2) : '-'}
                  </td>
                </tr>

                {/* Expanded Cash Details */}
                {cashExpanded && (
                  <tr className="bg-[#252538]">
                    <td colSpan={4} className="p-6">
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-sm font-medium text-white/80 mb-3">Cash movements</h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left py-2 text-white/60">Time</th>
                                <th className="text-left py-2 text-white/60">User</th>
                                <th className="text-right py-2 text-white/60">Amount (J$)</th>
                                <th className="text-left py-2 text-white/60 pl-4">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cashMovements && cashMovements.length > 0 ? (
                                cashMovements.map((movement) => (
                                  <tr key={movement.id} className="border-b border-white/5">
                                    <td className="py-2 text-white">
                                      {new Date(movement.created_at).toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true,
                                      })}
                                    </td>
                                    <td className="py-2 text-white">
                                      {movement.user 
                                        ? `${(movement.user as any).first_name || ''} ${(movement.user as any).last_name || ''}`.trim()
                                        : 'System'}
                                    </td>
                                    <td className="py-2 text-right text-white tabular-nums">
                                      {movement.amount.toFixed(2)}
                                    </td>
                                    <td className="py-2 text-white pl-4 capitalize">
                                      {movement.movement_type.replace(/_/g, ' ')}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="py-2 text-white">
                                    {openingTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </td>
                                  <td className="py-2 text-white">{cashierName}</td>
                                  <td className="py-2 text-right text-white tabular-nums">
                                    {(session.opening_balance || 0).toFixed(2)}
                                  </td>
                                  <td className="py-2 text-white pl-4">Opening float</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="grid grid-cols-2 gap-8 border-t border-white/10 pt-4">
                          <div className="flex justify-between">
                            <span className="text-white/60">Cash payments received</span>
                            <span className="text-white tabular-nums">{(session.cash_payments || 0).toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="space-y-3 border-t border-white/10 pt-4">
                          <div className="flex items-center justify-between">
                            <span className="text-white">Closing float</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={closingFloat}
                              onChange={(e) => setClosingFloat(e.target.value)}
                              className="w-28 text-center bg-[#2a2a3e] border-[#3a3a4e] text-white"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-white">Cash to bank</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={cashToBank}
                              onChange={(e) => setCashToBank(e.target.value)}
                              className="w-28 text-center bg-[#2a2a3e] border-[#3a3a4e] text-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Dynamic Payment Method Rows from Settlement Data */}
                {paymentBreakdownFormatted
                  .filter(pm => pm.method !== 'cash') // Cash is handled separately above
                  .map((paymentMethod) => {
                    const hasTypes = paymentMethod.types.length > 0;
                    const isExpanded = paymentMethod.method === 'card' ? cardExpanded : false;
                    const expectedAmount = paymentMethod.total;
                    
                    return (
                      <React.Fragment key={paymentMethod.method}>
                        {/* Payment Method Row */}
                        <tr className="border-b border-white/10">
                          <td className="py-4">
                            {hasTypes ? (
                              <button 
                                onClick={() => {
                                  if (paymentMethod.method === 'card') setCardExpanded(!cardExpanded);
                                }}
                                className="text-left flex items-center gap-2"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-white/60" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-white/60" />
                                )}
                                <div>
                                  <div className="font-medium text-white">{paymentMethod.methodLabel}</div>
                                  <div className="text-sm text-blue-400 hover:underline">
                                    {paymentMethod.count} transaction{paymentMethod.count !== 1 ? 's' : ''} • Click to view breakdown
                                  </div>
                                </div>
                              </button>
                            ) : (
                              <div>
                                <div className="font-medium text-white">{paymentMethod.methodLabel}</div>
                                <div className="text-sm text-white/60">
                                  {paymentMethod.count} transaction{paymentMethod.count !== 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="py-4 text-right text-white tabular-nums">
                            {expectedAmount.toFixed(2)}
                          </td>
                          <td className="py-4 text-center text-white tabular-nums">
                            {/* For non-cash methods, counted = expected (from payment processor) */}
                            {expectedAmount.toFixed(2)}
                          </td>
                          <td className="py-4 text-right text-white/60">-</td>
                        </tr>

                        {/* Expanded Payment Type Details */}
                        {isExpanded && hasTypes && (
                          <tr className="bg-[#252538]">
                            <td colSpan={4} className="p-6">
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-white/80 mb-3">
                                  {paymentMethod.methodLabel} Breakdown by Type
                                </h4>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-white/10">
                                      <th className="text-left py-2 text-white/60">Payment Type</th>
                                      <th className="text-right py-2 text-white/60">Transactions</th>
                                      <th className="text-right py-2 text-white/60">Amount (J$)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paymentMethod.types.map((type, idx) => (
                                      <tr key={type.type_id || idx} className="border-b border-white/5">
                                        <td className="py-2 text-white">{type.type_name}</td>
                                        <td className="py-2 text-right text-white/80">{type.count}</td>
                                        <td className="py-2 text-right text-white tabular-nums">
                                          {type.total.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t border-white/20">
                                      <td className="py-2 font-medium text-white">Total {paymentMethod.methodLabel}</td>
                                      <td className="py-2 text-right font-medium text-white">{paymentMethod.count}</td>
                                      <td className="py-2 text-right font-medium text-white tabular-nums">
                                        {paymentMethod.total.toFixed(2)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* Show placeholder if no card payments */}
                {!paymentBreakdownFormatted.some(pm => pm.method !== 'cash') && (
                  <>
                    <tr className="border-b border-white/10">
                      <td className="py-4 text-white">Card Payments</td>
                      <td className="py-4 text-right text-white tabular-nums">0.00</td>
                      <td className="py-4 text-center text-white tabular-nums">0.00</td>
                      <td className="py-4 text-right text-white/60">-</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 text-white">Gift Card</td>
                      <td className="py-4 text-right text-white tabular-nums">0.00</td>
                      <td className="py-4 text-center text-white tabular-nums">0.00</td>
                      <td className="py-4 text-right text-white/60">-</td>
                    </tr>
                  </>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-white/30">
                  <td className="py-4 font-semibold text-white">Totals</td>
                  <td className="py-4 text-right font-semibold text-white tabular-nums">
                    {totalExpected.toFixed(2)}
                  </td>
                  <td className="py-4 text-center font-semibold text-white tabular-nums">
                    {totalCounted.toFixed(2)}
                  </td>
                  <td className={cn(
                    "py-4 text-right font-semibold tabular-nums",
                    totalDifference > 0 && "text-green-400",
                    totalDifference < 0 && "text-red-400",
                    totalDifference === 0 && "text-white"
                  )}>
                    {totalDifference !== 0 ? totalDifference.toFixed(2) : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <hr className="border-white/10" />

        {/* Items Sold Summary */}
        {itemsSold && itemsSold.length > 0 && (
          <>
            <section className="grid grid-cols-[250px_1fr] gap-8">
              <div>
                <h2 className="text-lg font-medium">Items sold this shift</h2>
                <p className="text-white/60 text-sm mt-2">
                  Verify against physical stock count.
                </p>
              </div>
              
              <div className="bg-[#252538] rounded-lg p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
                  {itemsSold.map((item, i) => (
                    <div key={i} className="flex justify-between py-1">
                      <span className="text-white truncate mr-4">{item.name}</span>
                      <span className="text-white font-medium tabular-nums">{item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between font-semibold">
                  <span className="text-white">Total Items Sold</span>
                  <span className="text-white tabular-nums">
                    {itemsSold.reduce((sum, item) => sum + item.quantity, 0)}
                  </span>
                </div>
              </div>
            </section>

            <hr className="border-white/10" />
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4 pb-8">
          <Button
            variant="ghost"
            className="border border-white/30 text-white hover:bg-white/10 hover:text-white"
            onClick={() => navigate('/register-closures')}
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            className="border border-white/30 text-white hover:bg-white/10 hover:text-white gap-2"
            onClick={handlePrintSummary}
          >
            <Printer className="h-4 w-4" />
            Print Summary
          </Button>
          <Button
            variant="default"
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            onClick={handleClose}
            disabled={isClosing || closeRegister.isPending || !canSettleRegister}
          >
            {(isClosing || closeRegister.isPending) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !canSettleRegister ? (
              <ShieldAlert className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {!canSettleRegister ? 'Supervisor Required' : 'Settle Register'}
          </Button>
        </div>
      </div>

      {/* Manager PIN Dialog for Settlement Approval */}
      <ManagerPinDialog
        open={showSettlementPinDialog}
        onOpenChange={setShowSettlementPinDialog}
        onVerified={handleSettlementApproved}
        title="Settlement Approval Required"
        description="Enter manager PIN to settle this register and close the session"
      />
    </div>
  );
}
